import { capitalize, get } from 'lodash-es';
import { FieldErrors, FieldValues } from 'react-hook-form';
import { SetRequired } from 'type-fest';
import { setLocale } from 'yup';
import { AuthResponse, AxiosApiError } from 'api';
import {
  DEFAULT_GOOGLE_LOGIN_ERROR_MESSAGE,
  GOOGLE_LOGIN_ERROR_MESSAGE,
} from './constants';
import {
  AuthSuccessResponse,
  SocialAuthSuccessResponse,
  UserAuthenticationEventType,
  UserAuthResult,
} from './types';

const GOOGLE_ERROR_CODE_MESSAGES: Record<number, string> = {
  204: GOOGLE_LOGIN_ERROR_MESSAGE,
};

export function getGoogleAuthErrorMessage(err: AxiosApiError): string {
  return get(
    GOOGLE_ERROR_CODE_MESSAGES,
    err.response?.status ?? -1,
    DEFAULT_GOOGLE_LOGIN_ERROR_MESSAGE,
  );
}

/**
 * The API returns authentication data where the spareminToken and user are both
 * "optional".  There shouldn't be any reason why a successful authentication
 * request results in critical data missing from the response, but this function
 * will ensure that all of the required data is there.
 */
export function ensureAuthResponse<T extends AuthResponse>(
  response: T,
): SetRequired<T, 'spareminToken' | 'user'> {
  const { spareminToken, user } = response;

  if (!spareminToken || !user) {
    throw new Error('Auth response missing data');
  }

  return response as SetRequired<T, 'spareminToken' | 'user'>;
}

function isSocialAuthSuccessResponse(
  data: AuthSuccessResponse | SocialAuthSuccessResponse,
): data is SocialAuthSuccessResponse {
  return (data as SocialAuthSuccessResponse).userCreated !== undefined;
}

/**
 * Creates a UserAuthResult
 *
 * If `eventType` is passed, it is used for UserAuthResult.eventType, otherwise this
 * function will try to determine whether or not the user was created.
 *
 * When using social auth, the user might or might not have been created depending
 * on whether or not the account already exists, however with sparemin username/password
 * login, whether or not the user is created depends on whether the user submitted
 * the login form or the registration form. Passing `eventType` to this function is
 * useful in those cases as this utility does not know which form was submitted.
 */
export function createUserAuthResult(
  data: AuthSuccessResponse | SocialAuthSuccessResponse,
  eventType?: UserAuthenticationEventType,
): UserAuthResult {
  const userCreated = isSocialAuthSuccessResponse(data)
    ? data.userCreated
    : false;

  return {
    spareminToken: data.spareminToken,
    userId: data.user.id,
    eventType: eventType || (userCreated ? 'registration' : 'login'),
  };
}

export function initializeYup() {
  setLocale({
    mixed: {
      // eslint-disable-next-line no-template-curly-in-string
      required: ({ path }) => `${capitalize(path)} is required`,
    },
  });
}

export function isError<TFieldValues extends FieldValues>(
  error: FieldErrors<TFieldValues> | Error,
): error is Error {
  return error instanceof Error;
}

export function isFieldErrors<TFieldValues extends FieldValues>(
  error: FieldErrors<TFieldValues> | Error,
): error is FieldErrors<TFieldValues> {
  return !isError(error);
}

/**
 * Some of the elements in this library have an error callback that is invoked
 * with either a FieldError<T> or Error.  This utility will return the first
 * error string it finds in the error object.
 *
 * If the error object is an error, Error.message is used.
 *
 * If the error object is a FieldErrors<T>, then the errors are processed by
 * calling Object.keys on the error and returning the first message it finds.  If
 * finer control is needed, a `priority` array can be passed.  Keys with lower
 * indexes have higher priority than keys with higher indexes.  In this case, the
 * first error message that is found in priority order is returned.
 */
export function getErrorMessage<TFieldValues extends FieldValues>(
  error: FieldErrors<TFieldValues> | Error,
  priority?: Array<keyof TFieldValues>,
) {
  if (isError(error)) {
    return error.message;
  }

  const keysByPriority = priority ?? Object.keys(error);

  const messagesByPriority = keysByPriority
    .map((key) => error[key]?.message)
    .filter(Boolean);

  return messagesByPriority[0];
}
